import RosterBackendService from './RosterBackendService';
import {
    ChangeUserApplicationLanguageRequest,
    UserFeedStats,
    LookupUserRequest,
    ChangeUserPWordRequest,
    UserFeedRequestDTO,
    PasswordResetRequest,
    UserPasswordDTO,
    UserRole,
    UserAccountEmailSaveRequest,
    LookupUserDTOV1,
    PasswordResetAuditLogRequest,
    PasswordResetAuditLog,
    UserAccountDTO,
    ResettableAssessment,
    GetResettableAssessmentsRequest,
    ResetAssessmentsRequest,
    LessonProgressResetRequest,
    AcademicYearDTO,
} from '@beable/roster-backend-client-v1';

async function lookupUser(searchTerm: string, siteId: number, expanded?: boolean): Promise<LookupUserDTOV1[]> {
    const request: LookupUserRequest = {
        searchTerm: searchTerm,
        siteId: siteId,
        expanded: expanded
    }
    const response = await RosterBackendService.lookupUser(request);
    if (response.isOk()) {
        return response.value;
    }

    return [];
}

async function changeUserApplicationLanguage(userAccountId: string, userId: string, siteId: number, applicationLanguage: string): Promise<number> {
    const request: ChangeUserApplicationLanguageRequest =  {
        siteId,
        userAccountIds: [userAccountId],
        userIds: [userId],
        applicationLanguage
    };
    const response = await RosterBackendService.changeApplicationLanguage(request);
    if (response.isOk()) {
        return response.value;
    }
    return 0;
}

async function changeUserPassword(userId: string, password: string, siteId: number): Promise<boolean> {
    const req: ChangeUserPWordRequest = {
        userId: userId,
        password: password,
        siteId: siteId
    }

    const response = await RosterBackendService.changeUserPassword(req);
    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function resetPassword(siteId: number, userId: string): Promise<UserPasswordDTO[]> {
    const req: PasswordResetRequest = {
        userIds: [userId],
        siteId: siteId
    }

    const response = await RosterBackendService.resetPassword(req);
    if (response.isOk()) {
        return response.value.userPasswords;
    }

    return [];
}

async function getLastPasswordResetAuditLog(siteId: number, userId: string): Promise<PasswordResetAuditLog | undefined> {
    const req: PasswordResetAuditLogRequest = {
        userId: userId,
        siteId: siteId
    }

    const response = await RosterBackendService.getLastPasswordResetAuditLog(req);
    if (response.isOk()) {
        return response.value.log;
    }

    return undefined;
}

async function getUserFeedStats(userAccountId: string, siteId: number, languageCode: string): Promise<UserFeedStats | undefined> {
    const req: UserFeedRequestDTO = {
        userAccountId: userAccountId,
        siteId: siteId,
        languageCode: languageCode
    }

    const response = await RosterBackendService.getUserFeedStats(req);
    if (response.isOk()) {
        return response.value.stats;
    }
    return;
}

async function findRolesForUserAccount(userAccountId: string, siteId: number): Promise<UserRole[] | undefined> {
    const response = await RosterBackendService.findRolesForUser({ userAccountId: userAccountId, siteId: siteId });

    if (response.isOk()) {
        return response.value.roles;
    }
    return;
}

async function regenerateUserFeed(userAccountId: string, siteId: number, languageCode: string): Promise<boolean> {
    const req: UserFeedRequestDTO = {
        userAccountId: userAccountId,
        siteId: siteId,
        languageCode: languageCode
    }

    const response = await RosterBackendService.regenerateUserFeed(req);
    if (response.isOk()) {
        return true;
    }

    return false;
}

async function updateUserEmail(userAccountId: string, siteId: number, email: string): Promise<boolean> {
    const req: UserAccountEmailSaveRequest = {
        userAccountId: userAccountId,
        siteId: siteId,
        email: email
    }

    const response = await RosterBackendService.updateUserEmail(req);
    if (response.isOk()) {
        return true;
    }

    return false;
}

async function getResettableAssessments(userAccountId: string, userId: string, siteId: number, languageCode: string): Promise<{ assessments: ResettableAssessment[], academicYear: AcademicYearDTO | undefined }> {
    const req: GetResettableAssessmentsRequest = {
        siteId,
        userAccountId,
        userId,
        languageCode
    }

    const response = await RosterBackendService.getResettableAssessments(req);
    if (response.isOk()) {
        return response.value;
    }

    return { assessments: [], academicYear: undefined };
}

async function resetAssessments(userAccountId: string, siteId: number, languageCode: string, assessments: ResettableAssessment[], academicYear: AcademicYearDTO): Promise<boolean> {
    const req: ResetAssessmentsRequest = {
        userAccountId,
        siteId,
        languageCode,
        assessments,
        academicYear
    }

    const response = await RosterBackendService.resetAssessments(req);
    if (response.isOk()) {
        return true;
    }
    return false;
}

async function resetLessonProgress(userAccountId: string, siteId: number, academicYearId: string, lessonId: string, courseId: string, languageCode: string): Promise<boolean> {
    const req: LessonProgressResetRequest = {
        userAccountId: userAccountId,
        siteId: siteId,
        academicYearId: academicYearId,
        lessonId: lessonId,
        courseId: courseId,
        languageCode: languageCode,
    }

    const response = await RosterBackendService.resetLessonProgress(req);
    if (response.isOk()) {
        return true;
    }

    return false;
}

function toUserAccountDTO(user: LookupUserDTOV1) {
    return {
      siteId: user.siteId,
      id: user.userAccountId,
      userId: user.userId,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      primaryLanguage: user.primaryLanguage,
      applicationLanguage: user.applicationLanguage!,
      forcePasswordReset: false,
      passwordSetByRoster: user.passwordSetByRoster,
      grade: user.grade,
      email: user.email,
    } as UserAccountDTO;
}


export default {
    lookupUser,
    changeUserApplicationLanguage,
    changeUserPassword,
    resetPassword,
    getUserFeedStats,
    regenerateUserFeed,
    findRolesForUserAccount,
    updateUserEmail,
    getLastPasswordResetAuditLog,
    getResettableAssessments,
    resetAssessments,
    resetLessonProgress,
    toUserAccountDTO
};
